import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import type { BalanceItem, Currency } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { crypto, fiat, fiatSign, plural, shortAddress } from '@/shared/lib/format'
import { Button } from '@/shared/ui/Button'
import { CoinIcon } from '@/shared/ui/CoinIcon'
import { LineChart } from '@/shared/ui/LineChart'
import { Empty, Panel } from '@/shared/ui/Panel'
import { Section } from '@/shared/ui/Section'
import { Select } from '@/shared/ui/Select'
import { Strip } from '@/shared/ui/Strip'
import { Tile, toneOf } from '@/shared/ui/Tile'
import {
  ChevronDownIcon, ChevronRightIcon, DepositIcon, HistoryIcon, PlusIcon, WithdrawIcon,
} from '@/shared/ui/icons'
/*
  Заголовок правила берётся из AutoWithdrawalsPage, а не пишется здесь
  второй раз. Локальная копия печатала порог БЕЗ тикера: «При балансе
  от 0.5» на экране, где все остальные суммы в долларах, читается
  как пятьдесят центов вместо половины биткоина.
*/
import { ruleTitle } from '@/pages/app/AutoWithdrawalsPage'
import { CHART_PERIODS } from '@/stores/CabinetStore'
import { useCabinet, useUi } from '@/stores/RootStore'
import styles from './Overview.module.css'
import { useMeta } from '@/shared/lib/hooks/useMeta'

/**
 * Обзор: сводка, график дохода, балансы по валютам, автовыводы и кошельки.
 *
 * Всё грузится одним заходом при открытии. Отдельные ошибки не роняют
 * экран: не загрузился график — остальное всё равно показывается,
 * потому что балансы человеку нужнее графика.
 */
export const OverviewPage = observer(function OverviewPage() {
  useMeta('Обзор')
  const cabinet = useCabinet()

  useEffect(() => {
    void cabinet.loadOverview()
  }, [cabinet])

  const overview = cabinet.overview.value
  const sign = fiatSign(cabinet.view)

  return (
    <>
      <Section title="Обзор">
        <div className={styles.tiles}>
          <Tile
            label="Всего средств"
            value={overview ? fiat(overview.total_balance, cabinet.view) : '—'}
            loading={cabinet.overview.initialLoading}
          />
          <Tile
            label="Заработано сегодня"
            value={overview ? fiat(overview.today_income, cabinet.view) : '—'}
            note={overview ? changeNote(overview.today_income_change_percent) : undefined}
            tone={overview ? toneOf(overview.today_income_change_percent) : undefined}
            loading={cabinet.overview.initialLoading}
          />
          <Tile
            label="Сумма на выводе"
            value={overview ? fiat(overview.pending_withdrawal, cabinet.view) : '—'}
            note={plural(cabinet.pendingCount, 'заявка', 'заявки', 'заявок') + ' в обработке'}
            loading={cabinet.overview.initialLoading}
            to="/app/withdrawals"
          />
        </div>

        <Panel className={styles.chartPanel}>
          <div className={styles.chartHead}>
            <h3 className={styles.chartTitle}>График дохода</h3>
            <div className={styles.chartPeriod}>
              <Select
                ariaLabel="Период графика"
                placeholder="За неделю"
                options={CHART_PERIODS.map((p) => ({ value: p.value, label: p.label }))}
                value={String(cabinet.chartDays)}
                onChange={(value) => cabinet.setChartDays(Number(value))}
              />
            </div>
          </div>

          {cabinet.chart.error && !cabinet.chart.value ? (
            <Strip tone="warn">График не загрузился</Strip>
          ) : (
            <LineChart
              points={cabinet.chart.value?.points ?? []}
              sign={sign}
              label="График дохода по дням"
            />
          )}
        </Panel>
      </Section>

      <Coins />
      <Withdrawals />
    </>
  )
})

/** «На 12% больше чем вчера» — подпись плитки дословно по макету. */
export function changeNote(value: string | number): string {
  const number = typeof value === 'number' ? value : Number(value)
  const safe = Number.isFinite(number) ? number : 0
  const rounded = Math.abs(safe).toFixed(1).replace(/\.0$/, '')
  return `На ${rounded}% ${safe < 0 ? 'меньше' : 'больше'} чем вчера`
}

/*
  Валюты и Вывод экспортируются: админский обзор (Overview 4:28811)
  — это тот же экран с другими плитками и фильтром по аккаунтам
  на графике. Всё ниже графика в макете совпадает подпись в подпись,
  и держать вторую копию значит однажды получить две разные.
*/
export const Coins = observer(function Coins() {
  const cabinet = useCabinet()
  const ui = useUi()
  const navigate = useNavigate()
  const items = cabinet.balances.value?.items ?? []
  /*
    На телефоне у строки валюты раскрыта только одна — первая, как
    в макете: три кнопки на каждую из шести строк превращают экран
    в стену кнопок, и «Отправить» нажимается не у той монеты.
    На планшете и десктопе кнопки видны всегда, состояние не мешает.
  */
  const [open, setOpen] = useState<Currency | null>(null)
  // Балансы приходят после первого рендера, поэтому «первая строка»
  // вычисляется каждый раз, а не запоминается в начальном состоянии:
  // иначе на телефоне не раскрыта ни одна.
  const opened = open ?? items[0]?.currency ?? null

  return (
    <Section title="Валюты">
      {cabinet.balances.initialLoading ? (
        <div className={styles.skeleton} />
      ) : items.length === 0 ? (
        <Panel>
          <Empty title="Балансов пока нет" />
        </Panel>
      ) : (
        <>
          {/* Шапка таблицы — не в карточке: в макете это подписи колонок
              над списком, и они выровнены по тем же 12px внутреннего
              отступа, что и содержимое строк. */}
          <div className={styles.coinHead}>
            <span>Валюта и сеть</span><span>Баланс</span><span>Цена</span>
            <span className={styles.coinHeadActions}>Действия</span>
          </div>
          {items.map((item) => (
            <Coin key={item.currency} item={item}
                  sign={cabinet.view}
                  open={opened === item.currency}
                  onToggle={() => setOpen(opened === item.currency ? null : item.currency)}
                  onHistory={() => navigate(`/app/history?currency=${item.currency}`)}
                  onSend={() => ui.openDialog({ kind: 'send', currency: item.currency })}
                  onReceive={() => ui.openDialog({ kind: 'receive', currency: item.currency })} />
          ))}
        </>
      )}
    </Section>
  )
})

function Coin({ item, sign, open, onToggle, onHistory, onSend, onReceive }: {
  item: BalanceItem
  sign: 'usd' | 'rub'
  open: boolean
  onToggle: () => void
  onHistory: () => void
  onSend: () => void
  onReceive: () => void
}) {
  const coin = coinOf(item.currency)
  const network = networkOf(item.currency)
  const price = fiat(item.price, sign)
  return (
    <div className={open ? `${styles.coin} ${styles.coinOpen}` : styles.coin}>
      {/*
        На широких экранах обёртка «растворяется» (display: contents),
        и её дети встают в те же четыре колонки, что и подписи над
        списком. На телефоне она остаётся строкой — там у карточки
        своя раскладка: значок, название, баланс и стрелка.
      */}
      <div className={styles.coinMain}>
        <span className={styles.coinName}>
          <span className={styles.coinMark} aria-hidden="true">
            <CoinIcon currency={item.currency} size={24} />
          </span>
          <span className={styles.coinTitles}>
            <span className={styles.coinTicker}>{coin.ticker}</span>
            <span className={styles.coinTickerFull}>{coin.ticker} ({network})</span>
            <span className={styles.coinNetwork}>{network}</span>
            <span className={styles.coinPriceUnder}>{price}</span>
          </span>
        </span>

        <span className={styles.coinAmount}>
          <span>{crypto(item.balance)} {coin.ticker}</span>
          <span className={styles.coinFiat}>{fiat(item.balance_fiat, sign)}</span>
        </span>

        <span className={styles.coinPrice}>{price}</span>

        {/* Раскрывашка только для телефона: шире 768 кнопки видны всегда. */}
        <button type="button" className={styles.coinToggle} onClick={onToggle}
                aria-expanded={open}
                aria-label={`${coin.ticker}: действия`}>
          <ChevronDownIcon size={24} />
        </button>
      </div>

      <span className={styles.coinActions}>
        <Button variant="secondary" onClick={onHistory} title="История">
          <HistoryIcon size={24} /><span className={styles.btnLabel}>История</span>
        </Button>
        <Button variant="secondary" onClick={onSend} title="Отправить">
          <WithdrawIcon size={24} /><span className={styles.btnLabel}>Отправить</span>
        </Button>
        <Button onClick={onReceive} title="Получить">
          <DepositIcon size={24} /><span className={styles.btnLabel}>Получить</span>
        </Button>
      </span>
    </div>
  )
}

export const Withdrawals = observer(function Withdrawals() {
  const cabinet = useCabinet()
  const ui = useUi()
  const rules = cabinet.rules.value ?? []
  const wallets = cabinet.wallets.value ?? []
  const shown = wallets.slice(0, 3)
  const rest = wallets.length - shown.length
  // В макете на обзоре стоят ровно два правила и три кошелька:
  // это витрина раздела, а не список. Остальные — за кнопкой «Ещё N»,
  // иначе карточка обзора растёт вместе с числом правил и график
  // дохода уезжает за нижний край экрана.
  const shownRules = rules.slice(0, 2)
  const restRules = rules.length - shownRules.length

  return (
    <Section title="Вывод">
      <Panel
        title="Автовыводы"
        note="Настройка вывода средств по времени или по сумме на балансе."
        actions={
          <>
            {restRules > 0 ? (
              <Button variant="secondary" onClick={() => ui.openDialog({ kind: 'auto' })}
                      title={`Ещё ${restRules}`}>
                <span className={styles.btnLabel}>Ещё {restRules}</span>
                <ChevronRightIcon size={24} />
              </Button>
            ) : null}
            <Button onClick={() => ui.openDialog({ kind: 'auto' })} title="Добавить автовывод">
              <span className={styles.btnLabel}>Добавить</span><PlusIcon size={24} />
            </Button>
          </>
        }
      >
        {rules.length === 0 ? null : (
          <div className={styles.cards}>
            {shownRules.map((rule) => (
              <button key={rule.id} type="button" className={styles.mini}
                      onClick={() => ui.openDialog({ kind: 'auto' })}>
                <span className={styles.miniBody}>
                  <span className={styles.miniLabel}>
                    {rule.type === 'by_amount' ? 'Вывод по сумме' : 'Вывод по времени'}
                  </span>
                  <span className={styles.miniTitle}>{ruleTitle(rule)}</span>
                </span>
                <span className={rule.enabled
                  ? `${styles.miniState} ${styles.on}`
                  : `${styles.miniState} ${styles.off}`}>
                  <span className={styles.miniDot} />
                  <span>{rule.enabled ? 'Активен' : 'Отключён'}</span>
                </span>
                <span className={styles.miniGo}><ChevronRightIcon /></span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Кошельки"
        note="Подключённые адреса для автоматического вывода средств."
        actions={
          <>
            {rest > 0 ? (
              <Button variant="secondary" onClick={() => ui.openDialog({ kind: 'wallets' })}
                      title={`Ещё ${rest}`}>
                <span className={styles.btnLabel}>Ещё {rest}</span><ChevronRightIcon size={24} />
              </Button>
            ) : null}
            <Button onClick={() => ui.openDialog({ kind: 'wallets' })} title="Добавить кошелёк">
              <span className={styles.btnLabel}>Добавить</span><PlusIcon size={24} />
            </Button>
          </>
        }
      >
        {wallets.length === 0 ? null : (
          <div className={`${styles.cards} ${styles.three}`}>
            {shown.map((wallet) => {
              const coin = coinOf(wallet.currency)
              // Сеть в скобках — только у монет, живущих в нескольких сетях.
              // «BTC (Bitcoin)» — шум, «USDT (TRC20)» — единственный способ
              // не перепутать адреса, которые друг друга не принимают.
              const title = coin.networks.length > 1
                ? `${coin.ticker} (${networkOf(wallet.currency)})`
                : coin.ticker
              return (
                <button key={wallet.id} type="button" className={styles.mini}
                        onClick={() => ui.openDialog({ kind: 'wallets' })}>
                  <span className={styles.miniBody}>
                    <span className={styles.miniHead}>{title}</span>
                    <span className={styles.miniTitle}>{shortAddress(wallet.address, 6, 4)}</span>
                  </span>
                  <span className={styles.miniLabel}>{wallet.name}</span>
                  <span className={styles.miniGo}><ChevronRightIcon /></span>
                </button>
              )
            })}
          </div>
        )}
      </Panel>
    </Section>
  )
})
